import { useCallback, useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useBrand } from '../brand/context.js';
import { useLegal } from '../brand/legal-context.js';
import { I18nContext, type I18nValue } from './context.js';
import {
  createLocales,
  defaultLocaleOf,
  resolveLocale,
  type DictionaryBuilders,
  type Locale,
} from './registry.js';

const LOCALE_STORAGE_KEY = 'kitup:locale';

export function I18nProvider({
  children,
  dictionaries,
  locale: localeCode,
}: {
  readonly children: ReactNode;
  /** Словари локалей бренда. Приносит точка входа — см. `registry.ts`. */
  readonly dictionaries: DictionaryBuilders;
  readonly locale?: string | null;
}): JSX.Element {
  const brand = useBrand();
  /*
   * Словари строятся один раз на бренд: он не меняется за время жизни
   * приложения, а сборка словаря пробегает всё дерево строк.
   *
   * The dictionary needs the legal bundle for document names inside sentences
   * ("прочитали публичную оферту"). It comes from the neighbouring context
   * rather than off the brand, which does not carry it — see
   * `brand/legal-context.ts`.
   */
  const legal = useLegal();
  const locales = useMemo(
    () => createLocales(brand, legal, dictionaries),
    [brand, legal, dictionaries],
  );
  const fallback = useMemo(() => defaultLocaleOf(brand), [brand]);
  const [locale, setLocaleState] = useState<Locale>(() =>
    resolveLocale(locales, fallback, localeCode ?? readStoredLocale()),
  );
  const config = locales[locale];
  const setLocale = useCallback((nextLocale: Locale): void => {
    setLocaleState(nextLocale);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    } catch {
      // В приватном режиме хранилище может быть недоступно — язык всё равно меняется до перезагрузки.
    }
  }, []);
  /*
   * `resolve` и `available` зависят только от бренда, поэтому считаются
   * отдельно от `value` и переживают смену языка. Иначе смена языка меняла бы
   * их идентичность, эффект восстановления сессии в `AuthProvider` запускался
   * бы заново и возвращал язык профиля — переключатель в настройках откатывал
   * бы сам себя.
   */
  const resolve = useCallback(
    (input?: string | null): Locale => resolveLocale(locales, fallback, input),
    [fallback, locales],
  );
  const available = useMemo(() => Object.keys(locales) as Locale[], [locales]);
  const value = useMemo<I18nValue>(() => {
    const dateFormatter = new Intl.DateTimeFormat(config.dateLocale);
    return {
      locale,
      t: config.dictionary,
      available,
      setLocale,
      resolveLocale: resolve,
      formatDate: (input) => dateFormatter.format(toDate(input)),
    };
  }, [available, config, locale, resolve, setLocale]);

  useEffect(() => {
    const { meta } = config.dictionary;
    document.documentElement.lang = config.htmlLang;
    document.title = meta.title;
    /*
     * Общие SEO- и OG-теги стоят в `index.html` готовыми для краулеров без JS.
     * Публичная книга получает свой server-rendered блок, а её страница ещё раз
     * синхронизирует metadata после загрузки API.
     */
    setMetaContent('meta[name="description"]', meta.description);
    setMetaContent('meta[property="og:site_name"]', config.dictionary.app.name);
    setMetaContent('meta[property="og:title"]', meta.title);
    setMetaContent('meta[property="og:description"]', meta.description);
    setMetaContent('meta[property="og:image:alt"]', meta.ogImageAlt);
  }, [config]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function readStoredLocale(): string | null {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function setMetaContent(selector: string, content: string): void {
  document.querySelector(selector)?.setAttribute('content', content);
}

function toDate(input: Date | number | string): Date {
  if (input instanceof Date) return input;
  return typeof input === 'number' ? new Date(input) : new Date(input);
}
